import React, { useEffect, useState } from "react";
import { fetchAuditLogs } from "../services/adminAuditApi";
import "../styles/Admin.css";

function formatDate(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}

function safeStringifyMeta(meta) {
  if (!meta) return "";
  try {
    const s = JSON.stringify(meta);
    if (s.length > 120) {
      return s.slice(0, 117) + "...";
    }
    return s;
  } catch {
    return "[unserializable meta]";
  }
}

function AdminAuditPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const data = await fetchAuditLogs({ limit: 100, offset: 0 });
        if (!cancelled) {
          if (!data || data.ok !== true) {
            setError("Unable to load audit logs.");
            setItems([]);
          } else {
            setItems(Array.isArray(data.items) ? data.items : []);
          }
        }
      } catch (err) {
        console.error("[AdminAuditPage] load error:", err);
        if (!cancelled) {
          setError("Unable to load audit logs. Check your connection.");
          setItems([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
  };

  return (
    <div className="ucx-admin-page">
      <div className="ucx-admin-header">
        <div>
          <h1 className="ucx-admin-title">Audit Logs</h1>
          <p className="ucx-admin-subtitle">
            Timeline of important actions captured by SafeMatrix hooks.
          </p>
        </div>
        <div className="ucx-admin-header-right">
          <button
            type="button"
            className="ucx-btn ucx-btn-outline"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <span className="ucx-small">
            Showing latest {items.length || 0} events
          </span>
        </div>
      </div>

      {error && (
        <div className="ucx-admin-status ucx-admin-status-error">
          {error}
        </div>
      )}

      <div className="ucx-admin-table-wrapper">
        <table className="ucx-admin-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Event</th>
              <th>Entity</th>
              <th>User</th>
              <th>Meta</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="ucx-admin-empty">
                  No audit events yet.
                </td>
              </tr>
            )}

            {items.map((row) => (
              <tr key={row.id}>
                <td>{formatDate(row.createdAt)}</td>
                <td>{row.eventType}</td>
                <td>
                  {row.entityType}
                  {row.entityId != null ? ` #${row.entityId}` : ""}
                </td>
                <td>{row.userId != null ? `User #${row.userId}` : "Public"}</td>
                <td>
                  <span className="ucx-admin-meta-json">
                    {safeStringifyMeta(row.meta)}
                  </span>
                </td>
              </tr>
            ))}

            {loading && (
              <tr>
                <td colSpan={5} className="ucx-admin-empty">
                  Loading audit logs…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminAuditPage;
